/**
 * Version comparison utility for SEMVER strings
 *
 * Compares version strings in the format "major.minor.patch" (e.g., "3.4.15")
 * Returns -1 if current < minimum, 0 if equal, 1 if current > minimum
 */

/**
 * Compare two SEMVER version strings
 * @param current - Current app version (e.g., "3.4.15")
 * @param minimum - Minimum required version (e.g., "3.5.0")
 * @returns -1 if current < minimum, 0 if equal, 1 if current > minimum
 */
export const compareVersions = (current: string, minimum: string): number => {
  const parseVersion = (version: string): number[] => {
    // Strip leading 'v' or 'V' prefix (common in git tags like "v4.0.2")
    const normalized = version.replace(/^v/i, '');
    return normalized
      .split('.')
      .slice(0, 3)
      .map(segment => {
        const num = parseInt(segment, 10);
        return isNaN(num) ? 0 : num;
      });
  };

  const currentParts = parseVersion(current);
  const minimumParts = parseVersion(minimum);

  // Ensure both arrays have 3 elements
  while (currentParts.length < 3) currentParts.push(0);
  while (minimumParts.length < 3) minimumParts.push(0);

  for (let i = 0; i < 3; i++) {
    if (currentParts[i] < minimumParts[i]) return -1;
    if (currentParts[i] > minimumParts[i]) return 1;
  }

  return 0;
};

/**
 * Check if the current version meets the minimum required version
 * @param current - Current app version
 * @param minimum - Minimum required version
 * @returns true if current >= minimum, false otherwise
 */
export const isVersionValid = (current: string, minimum: string): boolean => {
  return compareVersions(current, minimum) >= 0;
};

/**
 * Check if an upgrade is required
 * @param current - Current app version
 * @param minimum - Minimum required version
 * @returns true if upgrade is required (current < minimum), false otherwise
 */
export const isUpgradeRequired = (current: string, minimum: string): boolean => {
  // If minimum is '0.0.0' or empty, no enforcement
  if (!minimum || minimum === '0.0.0') {
    return false;
  }

  return compareVersions(current, minimum) < 0;
};
